import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Save, GripVertical } from 'lucide-react';
import api from '../utils/api';
import { PROFILE_ICON_OPTIONS, profileIconMap } from '../utils/profileIcons';

const createItem = () => ({
  id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  label: 'Thông tin',
  value: '',
  icon: 'circle-help',
  url: '',
});

const reorderList = (list, fromIndex, toIndex) => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return list;
  const next = [...list];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export default function ProfileEditorModal({ onClose, onUpdated }) {
  const [form, setForm] = useState({
    name: '',
    username: '',
    bio: '',
    avatar_url: '/profile.jpg',
    items: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await api.getProfile();
        setForm({
          name: data.profile?.name || '',
          username: data.profile?.username || '',
          bio: data.profile?.bio || '',
          avatar_url: data.profile?.avatar_url || '/profile.jpg',
          items: Array.isArray(data.profile?.items) ? data.profile.items : [],
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const updateItem = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        items: form.items.filter((item) => item.value?.trim()),
      };
      await api.updateProfile(payload);
      onUpdated?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Đổi thông tin Profile</div>
          <button className="btn-icon" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="modal-body spinner-wrap"><div className="spinner" /></div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body stack-gap-lg">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tên hiển thị</label>
                  <input className="form-input" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input className="form-input" value={form.username} onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))} required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Bio</label>
                <textarea className="form-textarea" rows={3} value={form.bio} onChange={(e) => setForm((prev) => ({ ...prev, bio: e.target.value }))} />
              </div>

              <div className="form-group">
                <label className="form-label">Avatar URL</label>
                <input className="form-input" value={form.avatar_url} onChange={(e) => setForm((prev) => ({ ...prev, avatar_url: e.target.value }))} placeholder="/profile.jpg" />
                <div className="form-hint">Đang dùng mặc định là /profile.jpg theo file bạn gửi.</div>
              </div>

              <div className="profile-items-head">
                <div>
                  <div className="detail-section-title">Các dòng thông tin ở sidebar</div>
                  <div className="form-hint">Có thể giữ và kéo từng dòng để đổi thứ tự hiển thị.</div>
                </div>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setForm((prev) => ({ ...prev, items: [...prev.items, createItem()] }))}>
                  <Plus size={14} /> Thêm dòng mới
                </button>
              </div>

              <div className="profile-item-list">
                {form.items.map((item, index) => {
                  const PreviewIcon = profileIconMap[item.icon] || profileIconMap['circle-help'];
                  return (
                    <div
                      className={`profile-item-editor ${dragIndex === index ? 'dragging' : ''}`}
                      key={item.id || index}
                      draggable
                      onDragStart={() => setDragIndex(index)}
                      onDragEnd={() => setDragIndex(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragIndex == null) return;
                        setForm((prev) => ({ ...prev, items: reorderList(prev.items, dragIndex, index) }));
                        setDragIndex(null);
                      }}
                    >
                      <button className="btn-icon profile-item-drag-handle" type="button" title="Giữ để kéo đổi vị trí">
                        <GripVertical size={16} />
                      </button>
                      <div className="profile-item-icon-preview">
                        <PreviewIcon size={16} />
                      </div>
                      <div className="profile-item-editor-grid">
                        <input className="form-input" value={item.label} onChange={(e) => updateItem(index, 'label', e.target.value)} placeholder="Nhãn" />
                        <input className="form-input" value={item.value} onChange={(e) => updateItem(index, 'value', e.target.value)} placeholder="Nội dung hiển thị" />
                        <select className="form-select" value={item.icon} onChange={(e) => updateItem(index, 'icon', e.target.value)}>
                          {PROFILE_ICON_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <input className="form-input" value={item.url || ''} onChange={(e) => updateItem(index, 'url', e.target.value)} placeholder="Link mở khi bấm (không bắt buộc)" />
                      </div>
                      <button className="btn-icon danger-icon" type="button" onClick={() => setForm((prev) => ({ ...prev, items: prev.items.filter((_, itemIndex) => itemIndex !== index) }))}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
                {form.items.length === 0 && <div className="empty-state">Chưa có dòng thông tin nào.</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" type="button" onClick={onClose}>Huỷ</button>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                <Save size={15} /> {saving ? 'Đang lưu...' : 'Lưu Profile'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
